import { Label, Input, Select } from "@tellescope/design-system";
import { Button } from "@tellescope/ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn, levenshteinDistance } from "@tellescope/utils";
import { MedicationResponse } from "@tellescope/types-models";
import { UNKNOWN_DRUG_NAME } from "../../utils";
import { CanvasMedicationInstructions } from "./CanvasMedicationInstructions";
import { EMPTY_MEDICATION } from "./empty-medication";
import { MedicationDosageFields } from "./MedicationDosageFields";
import { useMedicationCatalog } from "./useMedicationCatalog";

export interface AddMedicationFormProps {
  medication?: MedicationResponse;
  onChange: (medication: MedicationResponse) => void;
  onRemove?: () => void;
  className?: string;
  dataSource?: string;
  autoFocus?: boolean;
  required?: boolean;
  fieldId?: string;
}

export const AddMedicationForm = ({
  medication: providedMedication,
  onChange,
  onRemove,
  className,
  dataSource,
  autoFocus,
  required,
  fieldId,
}: AddMedicationFormProps) => {
  const { t } = useTranslation("forms");
  const storedMedication: MedicationResponse = {
    ...providedMedication,
    displayTerm: providedMedication?.displayTerm ?? "",
    drugName: providedMedication?.drugName ?? "",
  };

  const {
    isCanvas,
    searchQuery,
    setSearchQuery,
    allDisplayNames,
    specificDrugOptions,
    rxNormCode,
    ndcCodes,
    isLoadingSpecificDrugs,
    canvasOptions,
  } = useMedicationCatalog({
    selectedDisplayName: storedMedication.displayTerm,
    selectedSpecificDrugName: storedMedication.drugName,
    dataSource,
  });

  const isDrugUnresolvable =
    !isCanvas && !!storedMedication.displayTerm && !isLoadingSpecificDrugs && !specificDrugOptions.length;

  const medication: MedicationResponse = storedMedication.drugName
    ? {
        ...storedMedication,
        rxNormCode: rxNormCode?.[0] ?? storedMedication.rxNormCode,
        NDCs: ndcCodes?.length ? ndcCodes.map(String) : storedMedication.NDCs,
      }
    : isDrugUnresolvable
    ? { ...storedMedication, drugName: UNKNOWN_DRUG_NAME }
    : storedMedication;

  const selectedSpecificDrug = useMemo(() => {
    return specificDrugOptions.find((drug) => drug.name === medication.drugName);
  }, [medication.drugName, specificDrugOptions]);

  const [displayTermSearch, setDisplayTermSearch] = useState("");
  const [drugSearch, setDrugSearch] = useState("");

  const displayTermResults = useMemo(() => {
    if (isCanvas) return allDisplayNames;
    const lowerSearch = displayTermSearch.toLowerCase();
    if (!lowerSearch) return allDisplayNames.slice(0, 50);
    return allDisplayNames
      .filter((displayName) => displayName.toLowerCase().includes(lowerSearch))
      .sort((a, b) => a.length - b.length)
      .slice(0, 50);
  }, [isCanvas, allDisplayNames, displayTermSearch]);

  const drugResults = useMemo(() => {
    const lowerSearch = drugSearch.toLowerCase();
    if (!lowerSearch) return specificDrugOptions.slice(0, 50);
    const searchable = (drug: (typeof specificDrugOptions)[number]) => `${drug.synonym}|${drug.name}|`.toLowerCase();
    return specificDrugOptions
      .filter((drug) => searchable(drug).includes(lowerSearch))
      .sort(
        (a, b) => levenshteinDistance(searchable(b), lowerSearch) - levenshteinDistance(searchable(a), lowerSearch)
      );
  }, [specificDrugOptions, drugSearch]);

  const handleDisplayTermChange = (displayTerm: string) => {
    if (displayTerm === medication.displayTerm) return;
    if (!isCanvas) return onChange({ ...EMPTY_MEDICATION, displayTerm });
    const selected = canvasOptions?.find((canvasOption) => canvasOption.display === displayTerm);
    onChange({ ...EMPTY_MEDICATION, displayTerm, drugName: displayTerm, fdbCode: selected?.code });
  };

  const handleDrugChange = (drugName: string) => {
    const selected = specificDrugOptions.find((drug) => drug.name === drugName);
    onChange({
      ...medication,
      drugName,
      drugSynonym: selected?.synonym,
      rxNormCode: selected ? String(selected.rxcui) : undefined,
    });
  };

  const displayTermId = fieldId ? `${fieldId}-displayTerm` : undefined;
  const drugSelectId = fieldId ? `${fieldId}-drugName` : undefined;
  const otherDrugId = fieldId ? `${fieldId}-otherDrug` : undefined;

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="space-y-1">
        <Label htmlFor={displayTermId} required={required}>
          {t("Search")}
        </Label>
        {/* Filtering is done here rather than by the Select so the RxNorm catalog can be capped at
            50 results; Canvas results come pre-filtered from the server. */}
        <Select
          modal
          clearable
          value={medication.displayTerm}
          onValueChange={(displayTerm) => handleDisplayTermChange(displayTerm ?? "")}
        >
          <Select.Trigger id={displayTermId} autoFocus={autoFocus}>
            <Select.Value placeholder={t("Choose a medication...")} fallback={medication.displayTerm} />
          </Select.Trigger>
          <Select.Content
            filter="external"
            search={isCanvas ? searchQuery : displayTermSearch}
            onSearchChange={isCanvas ? setSearchQuery : setDisplayTermSearch}
            loading={isCanvas ? isLoadingSpecificDrugs : allDisplayNames.length === 0}
            loadingText={t("Loading medications...")}
            searchPlaceholder={t("Search medications...")}
            emptyText={t("No medications found")}
          >
            {displayTermResults.map((displayName) => (
              <Select.Item key={displayName} value={displayName}>
                {displayName}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </div>
      {!!medication.displayTerm && !medication.otherDrug && !!specificDrugOptions.length && (
        <div className="space-y-1">
          <Label htmlFor={drugSelectId} required={required}>
            {t("Drug Select")}
          </Label>
          <Select
            modal
            clearable
            value={medication.drugName}
            onValueChange={(drugName) => handleDrugChange(drugName ?? "")}
          >
            <Select.Trigger id={drugSelectId}>
              <Select.Value
                placeholder={t("Drug Select")}
                fallback={selectedSpecificDrug?.synonym ?? medication.drugName}
              />
            </Select.Trigger>
            <Select.Content
              filter="external"
              search={drugSearch}
              onSearchChange={setDrugSearch}
              searchPlaceholder={t("Search specific drugs...")}
              emptyText={t("No specific drugs found")}
            >
              {drugResults.map((drug) => (
                <Select.Item key={drug.name} value={drug.name}>
                  {drug.synonym}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </div>
      )}
      {!!medication.displayTerm && (
        <>
          {(isDrugUnresolvable || !!medication.otherDrug) && (
            <div className="space-y-1">
              <Label htmlFor={otherDrugId} required>
                {t("Other Drug")}
              </Label>
              <Input
                id={otherDrugId}
                value={medication.otherDrug}
                onChange={(e) => onChange({ ...medication, otherDrug: e.target.value })}
                placeholder={t("Other Drug")}
              />
            </div>
          )}
          {isCanvas ? (
            <CanvasMedicationInstructions medication={medication} onChange={onChange} fieldId={fieldId} />
          ) : (
            <MedicationDosageFields medication={medication} onChange={onChange} fieldId={fieldId} />
          )}
          {!!onRemove && (
            <div className="w-full flex justify-end">
              <Button variant="outline" onClick={onRemove}>
                {t("Remove Medication")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
